import { css } from 'astroturf';
import Prism from 'prismjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { trackCTA, withRef } from '../../utils/track';

const EDITOR_FONT = "'Operator Mono', 'Fira Code', Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace";
const EDITOR_FONT_SIZE = '1rem';
const EDITOR_LINE_HEIGHT = '1.5rem';
const EDITOR_LETTER_SPACING = '0.2px';
const EDITOR_PADDING = 16;
const EDITOR_LINE_HEIGHT_PX = 24;

const styles = css`
  .demo {
    background: linear-gradient(140deg, rgba(45, 124, 216, 0.14), rgba(21, 158, 124, 0.14));
    border-radius: 16px;
    box-shadow: rgba(10, 37, 99, 0.2) 0.3em 0.3em 2em inset;
    padding: 1.5rem;
    margin: 1.75rem 0;
  }

  .topBar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0 0 0.8rem;
  }

  .topBarActions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
  }

  .layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.9rem;
    align-items: stretch;
  }

  .codePane {
    min-width: 0;
    container-type: inline-size;
  }

  .codeBlock {
    position: relative;
    height: var(--code-height, 16rem);
    max-height: calc(100cqw * 9 / 16);
    border-radius: 10px;
    overflow: hidden;
    background: #161835;

    & :global(.gatsby-highlight) {
      margin: 0;
      min-height: 100%;
    }

    & :global(pre[class*='language-']) {
      margin: 0;
      padding: 1rem;
      height: 100%;
      line-height: ${EDITOR_LINE_HEIGHT};
      font-size: ${EDITOR_FONT_SIZE};
      font-family: ${EDITOR_FONT};
      letter-spacing: ${EDITOR_LETTER_SPACING};
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      overflow-x: hidden;
      overflow-y: visible;
      background: transparent;
      tab-size: 4;
    }

    & :global(pre code) {
      display: block;
      font-size: ${EDITOR_FONT_SIZE};
      line-height: ${EDITOR_LINE_HEIGHT};
      font-family: ${EDITOR_FONT};
      letter-spacing: ${EDITOR_LETTER_SPACING};
      white-space: inherit;
      overflow-wrap: inherit;
      tab-size: 4;
    }
  }

  .copyButton {
    position: absolute;
    top: 0.6rem;
    right: 0.6rem;
    z-index: 3;
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: rgba(11, 19, 45, 0.82);
    color: #f2f8ff;
    border-radius: 999px;
    padding: 0.3rem 0.7rem;
    font-size: 0.72rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease-in-out;

    &:hover {
      background: rgba(19, 31, 70, 0.92);
    }
  }

  .codeEditor {
    position: absolute;
    inset: 0;
    border: 0;
    background: transparent;
    color: transparent;
    resize: none;
    outline: none;
    padding: 1rem;
    font-family: ${EDITOR_FONT};
    font-size: ${EDITOR_FONT_SIZE};
    line-height: ${EDITOR_LINE_HEIGHT};
    letter-spacing: ${EDITOR_LETTER_SPACING};
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    overflow-x: hidden;
    overflow-y: auto;
    caret-color: #f5fbff;
    z-index: 2;
    tab-size: 4;
  }

  .highlightScroll {
    position: absolute;
    inset: 0;
    overflow-x: hidden;
    overflow-y: auto;
    pointer-events: none;
    z-index: 1;
  }

  .codeEditor::selection {
    background: #f5fbff;
    color: #0f132f;
  }

  .previewPane {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .title {
    margin: 0 !important;
    font-size: 0.95rem;
    line-height: 1.2;
    font-weight: 700;
    color: #17344d;
  }

  .randomButton {
    background: rgba(255, 255, 255, 0.6);
    border: 1.5px solid rgba(14, 19, 35, 0.18);
    color: #16335c;
    border-radius: 8px;
    padding: 0.4rem 0.7rem;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    transition: background 0.15s;

    &:active svg {
      scale: 0.75;
      transform: rotate(-10deg);
    }

    &:hover {
      background: rgba(255, 255, 255, 0.85);
    }
  }

  @keyframes spinOneAndHalf {
    from {
      rotate: 0deg;
    }
    to {
      rotate: 360deg;
    }
  }

  .randomIcon {
    will-change: rotate, scale, transform;
    transition:
      scale 0.15s ease-in-out,
      transform 0.15s ease-in-out;
    animation: spinOneAndHalf 0.5s ease-out both;
  }

  .randomButtonHidden {
    opacity: 0;
    pointer-events: none;
  }

  .hideButton {
    composes: randomButton;
  }

  .showButton {
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: rgba(11, 19, 45, 0.78);
    color: #f2f8ff;
    border-radius: 999px;
    padding: 0.55rem 1rem;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease-in-out;

    &:hover {
      background: rgba(19, 31, 70, 0.9);
    }
  }

  .unsupportedNotice {
    border: 1px solid rgba(16, 29, 54, 0.16);
    border-radius: 10px;
    background: #0f1328;
    padding: 0.9rem 1rem;
    color: #fff;
    line-height: 1.5;
    text-align: center;
  }

  .previewFrame {
    width: 100%;
    aspect-ratio: 16 / 9;
    container-type: inline-size;
    border-radius: 10px;
    overflow: hidden;
    background: #0f1328;
    display: grid;
    place-items: center;
  }

  .previewCanvas {
    width: 1200px;
    height: 675px;
    transform-origin: 0 0;
  }

  .previewFrame {
    & iframe {
      width: 100%;
      height: 100%;
      border: 0;
      display: block;
      background: transparent;
    }
  }
`;

const parseCSS = (cssCode, polypaneVersion) => {
  if (polypaneVersion === null || polypaneVersion > 29) {
    return cssCode;
  }
  // Add any fallback or modification for older versions if needed
  return cssCode.replaceAll('element-scoped', '--element-scoped');
};

export function RandomCssDemo({ title, htmlBody, cssCodeInit = '', scaffoldCss, showLoadButton = true }) {
  const [cssCode, setCssCode] = useState(cssCodeInit);
  const [seed, setSeed] = useState(0);
  const [randomizeKey, setRandomizeKey] = useState(0);
  const [showPreview, setShowPreview] = useState(() => !showLoadButton);
  const [copied, setCopied] = useState(false);
  const highlightScrollRef = useRef(null);
  const copyResetRef = useRef(null);
  const [supportsRandom, setSupportsRandom] = useState(true);
  const [polypaneVersion, setPolypaneVersion] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.CSS || typeof window.CSS.supports !== 'function') {
      setSupportsRandom(false);
      return;
    }

    // Mirrors an @supports-style check for random() support.
    setSupportsRandom(window.CSS.supports('top: random(0%, 100%)'));

    const PolypaneVersion = window.navigator.userAgent.match(/Polypane\/(\d+)/);
    if (PolypaneVersion) {
      setPolypaneVersion(parseInt(PolypaneVersion[1], 10));
    }
  }, []);

  useEffect(() => {
    if (!showLoadButton) setShowPreview(true);
  }, [showLoadButton]);

  const codeHeightPx = useMemo(() => {
    const lines = Math.max(1, cssCode.split('\n').length);
    return lines * EDITOR_LINE_HEIGHT_PX + EDITOR_PADDING * 2;
  }, [cssCode]);

  useEffect(() => {
    Prism.highlightAll();
  }, [cssCode]);

  useEffect(
    () => () => {
      if (copyResetRef.current) {
        clearTimeout(copyResetRef.current);
      }
    },
    []
  );

  const srcDoc = useMemo(
    () => `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      * { box-sizing: border-box; }
    html { overflow: hidden; }
    body {margin: 0;}

      ${parseCSS(scaffoldCss, polypaneVersion)}

      ${parseCSS(cssCode, polypaneVersion)}
    </style>
  </head>
  <body>
    ${htmlBody}
  </body>
</html>`,
    [cssCode, htmlBody, scaffoldCss, seed, polypaneVersion]
  );

  const handleCopyCode = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(srcDoc);
      } else if (typeof document !== 'undefined') {
        const textArea = document.createElement('textarea');
        textArea.value = srcDoc;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      } else {
        return;
      }

      setCopied(true);
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
      copyResetRef.current = setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      // Ignore clipboard write errors and keep the default button label.
    }
  };

  return (
    <div className={styles.demo}>
      <div className={styles.topBar}>
        <h4 className={styles.title}>{title}</h4>

        <div className={styles.topBarActions}>
          <button
            type="button"
            className={`${styles.randomButton} ${!supportsRandom || !showPreview ? styles.randomButtonHidden : ''}`}
            onClick={() => {
              setRandomizeKey((current) => current + 1);
              setSeed((current) => current + 1);
            }}
            aria-hidden={!supportsRandom || !showPreview}
            tabIndex={!supportsRandom || !showPreview ? -1 : undefined}
          >
            <svg
              key={randomizeKey}
              className={styles.randomIcon}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Randomize
          </button>

          {showLoadButton && showPreview && supportsRandom && (
            <button type="button" className={styles.hideButton} onClick={() => setShowPreview(false)}>
              Hide Demo
            </button>
          )}
        </div>
      </div>

      <div className={styles.layout}>
        <div className={styles.previewPane}>
          {supportsRandom ? (
            <div className={styles.previewFrame}>
              {showPreview ? (
                <div className={styles.previewCanvas} style={{ transform: `scale(min(1, calc(100cqw / 1200px)))` }}>
                  <iframe title={title} srcDoc={srcDoc} key={seed} />
                </div>
              ) : (
                showLoadButton && (
                  <button type="button" className={styles.showButton} onClick={() => setShowPreview(true)}>
                    Show Demo
                  </button>
                )
              )}
            </div>
          ) : (
            <div className={styles.unsupportedNotice}>
              This browser does not support CSS <code>random()</code> yet. Try these demos in{' '}
              <a
                href={withRef('https://dashboard.polypane.app/register/', 'css-random-demo')}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackCTA('css-random-demo', { label: 'Polypane' })}
              >
                Polypane
              </a>
              .
            </div>
          )}
        </div>

        {cssCodeInit.length > 0 && (
          <div className={styles.codePane}>
            <div className={styles.codeBlock} style={{ '--code-height': `${codeHeightPx}px` }}>
              <button type="button" className={styles.copyButton} onClick={handleCopyCode}>
                {copied ? 'Copied! 🎉' : 'copy code'}
              </button>
              <div className={styles.highlightScroll} ref={highlightScrollRef}>
                <div className="gatsby-highlight">
                  <pre className="language-css">
                    <code>{cssCode}</code>
                  </pre>
                </div>
              </div>
              <textarea
                className={styles.codeEditor}
                spellCheck={false}
                wrap="soft"
                value={cssCode}
                onScroll={(event) => {
                  if (highlightScrollRef.current) {
                    highlightScrollRef.current.scrollTop = event.target.scrollTop;
                  }
                }}
                onChange={(event) => setCssCode(event.target.value)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
